<template>
  <div class="p-6 md:p-12 bg-gray-900 min-h-screen">
    <!-- Header -->
    <div class="text-center mb-12">
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-2">{{ title }}</h1>
      <p class="text-gray-400 text-lg">{{ subtitle }}</p>
    </div>

    <!-- Loading / Error -->
    <div v-if="isLoading" class="text-center text-lg py-20 text-white">
      Loading images...
    </div>
    <div v-else-if="error" class="text-center text-lg py-20 text-red-400">
      Error: {{ error }}
    </div>

    <!-- Masonry Columns -->
    <div v-else class="flex gap-4 justify-center">
      <div
          v-for="(column, colIndex) in columns"
          :key="colIndex"
          class="flex flex-col gap-4 w-full max-w-[240px]"
      >
        <div
            v-for="(item, itemIndex) in column"
            :key="item.id"
            class="image-item overflow-hidden rounded-lg shadow-xl cursor-pointer transition-transform duration-300 hover:scale-105"
            draggable="true"
            @dragstart="onDragStart(item, colIndex, itemIndex)"
            @dragover.prevent
            @drop="onDrop(item, colIndex, itemIndex)"
        >
          <img
              :src="getFullImageUrl(item.imageurl)"
              :alt="item.title"
              @error="onImageError(item)"
              class="w-full block object-cover"
              @click="openLightbox(item)"
          />
        </div>
      </div>
    </div>

    <!-- Lightbox Overlay with Navigation -->
    <transition name="fade">
      <div
          v-if="lightboxVisible"
          @click.self="closeLightbox"
          class="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 cursor-pointer"
      >
        <!-- Prev Button -->
        <button
            @click.stop="prevImage"
            class="absolute left-4 text-white text-3xl font-bold bg-black bg-opacity-40 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-60"
        >‹</button>

        <!-- Image -->
        <img
            :src="lightboxImage.imageurl"
            class="max-h-[90vh] max-w-[90vw] rounded shadow-xl transform transition-transform duration-300 scale-95 hover:scale-100"
            alt="Enlarged"
        />

        <!-- Next Button -->
        <button
            @click.stop="nextImage"
            class="absolute right-4 text-white text-3xl font-bold bg-black bg-opacity-40 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-60"
        >›</button>

        <!-- Close Button -->
        <button
            @click.stop="closeLightbox"
            class="absolute top-4 right-4 text-white text-2xl font-bold bg-black bg-opacity-40 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-60"
        >×</button>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: "AdminDashboard",
  props: {
    apiEndpoint: { type: String, required: true },
    title: { type: String, default: "Gallery" },
    subtitle: { type: String, default: "A collection of our best work." },
  },
  data() {
    return {
      items: [],
      isLoading: true,
      error: null,
      columns: [],
      columnHeights: [],
      columnsCount: 5,
      dragItem: null,
      dragColIndex: null,
      dragItemIndex: null,
      token: localStorage.getItem('token') || '',
      lightboxVisible: false,
      lightboxImage: null,
      flatItems: [], // flattened array for navigation
    };
  },
  methods: {
    getFullImageUrl(imagePath) {
      if (!imagePath) return "/images/default_movie.jpg";
      if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
      return imagePath;
    },
    onImageError(item) {
      item.imageurl = this.getFullImageUrl(null);
    },
    openLightbox(item) {
      this.lightboxImage = item;
      this.lightboxVisible = true;
    },
    closeLightbox() {
      this.lightboxVisible = false;
      this.lightboxImage = null;
    },
    prevImage() {
      const index = this.flatItems.findIndex(i => i.id === this.lightboxImage.id);
      const prevIndex = (index - 1 + this.flatItems.length) % this.flatItems.length;
      this.lightboxImage = this.flatItems[prevIndex];
    },
    nextImage() {
      const index = this.flatItems.findIndex(i => i.id === this.lightboxImage.id);
      const nextIndex = (index + 1) % this.flatItems.length;
      this.lightboxImage = this.flatItems[nextIndex];
    },
    async fetchImages() {
      this.isLoading = true;
      try {
        const response = await axios.get(this.apiEndpoint);
        let fetchedItems = response.data;

        fetchedItems.sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
        this.items = fetchedItems;
        this.flatItems = [...fetchedItems];
        this.updateColumns();
      } catch (e) {
        this.error = e.message || 'Failed to fetch images';
      } finally {
        this.isLoading = false;
      }
    },
    updateColumns() {
      const width = window.innerWidth;
      if (width >= 1200) this.columnsCount = 5;
      else if (width >= 992) this.columnsCount = 4;
      else if (width >= 768) this.columnsCount = 3;
      else if (width >= 480) this.columnsCount = 2;
      else this.columnsCount = 1;

      this.columns = Array.from({ length: this.columnsCount }, () => []);
      this.columnHeights = Array(this.columnsCount).fill(0);

      this.items.forEach((item) => {
        const height = item.height || 200;
        const shortestCol = this.columnHeights.indexOf(Math.min(...this.columnHeights));
        this.columns[shortestCol].push(item);
        this.columnHeights[shortestCol] += height;
      });
    },
    handleResize() {
      this.updateColumns();
    },

    // Drag & Drop
    onDragStart(item, colIndex, itemIndex) {
      this.dragItem = item;
      this.dragColIndex = colIndex;
      this.dragItemIndex = itemIndex;
    },
    async onDrop(targetItem, colIndex, itemIndex) {
      if (!this.dragItem) return;

      const sourceCol = this.columns[this.dragColIndex];
      const targetCol = this.columns[colIndex];

      sourceCol.splice(this.dragItemIndex, 1);
      targetCol.splice(itemIndex, 0, this.dragItem);

      this.items = this.columns.flat();
      this.flatItems = [...this.items]; // update flat array

      this.dragItem = null;
      this.dragColIndex = null;
      this.dragItemIndex = null;

      await this.updateOrderOnServer();
      this.updateColumns();
    },
    async updateOrderOnServer() {
      try {
        const orderedIds = this.items.map(item => item.id);
        const sectionName = this.apiEndpoint.split('/').pop();

        await axios.put(`http://localhost/api/${sectionName}/reorder`, { orderedIds }, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
      } catch (err) {
        console.error('Failed to update order', err);
      }
    },
  },
  mounted() {
    this.fetchImages();
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
.image-item img {
  width: 100%;
  display: block;
  border-radius: 0.5rem;
  object-fit: cover;
}

/* Lightbox fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
